import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { createQuery } from 'react-query-kit';

import { client } from '@/api/common';

export type EarningsMonth = {
  month: string;
  dailyRentalsCents: number;
  subscriptionsCents: number;
  deliveryCents: number;
  extrasDailyRentalsCents: number;
  extrasSubscriptionsCents: number;
  otherDailyRentalsCents: number;
  otherSubscriptionsCents: number;
  missedEarningsCents: number;
  extrasDailyRentalsCents?: number;
  extrasSubscriptionsCents?: number;
  otherDailyRentalsCents?: number;
  otherSubscriptionsCents?: number;
};

export type PerformanceChartMonthValues = {
  dailyRentalsCents: number;
  subscriptionsCents: number;
  deliveryCents: number;
  missedEarningsCents: number;
};

/** Display buckets: base rental + extras + other per trip type (from performance API). */
export function performanceChartEarningsMonth(
  month: EarningsMonth
): PerformanceChartMonthValues {
  return {
    dailyRentalsCents:
      month.dailyRentalsCents +
      month.extrasDailyRentalsCents +
      month.otherDailyRentalsCents,
    subscriptionsCents:
      month.subscriptionsCents +
      month.extrasSubscriptionsCents +
      month.otherSubscriptionsCents,
    deliveryCents: month.deliveryCents,
    missedEarningsCents: month.missedEarningsCents,
  };
}

export type MissedEarningsTrip = {
  reservationId: number;
  tripDate: string;
  carId: number;
  /** `{year} {brand} {model} ({plate})` from performance API. */
  carName: string;
  licensePlate: string;
  vehicleYear: number | null;
  brandName: string;
  modelName: string;
  missedEarningsCents: number;
};

export type MissedEarnings = {
  totalCents: number;
  /** Host-cancel missed trips in period. */
  tripCount: number;
  /** Completed trips in period + host-cancel missed trips. */
  bookedTripCount: number;
  showCancellationRateComparison: boolean;
  cancellationRate: number | null;
  previousCancellationRate: number | null;
  previousBookedTripCount: number | null;
  trips: MissedEarningsTrip[];
};

export type RatingWindow = {
  averageScore: number | null;
  reviewCount: number;
  pickupAndReturnRating: number | null;
  carConditionRating: number | null;
  cleanlinessRating: number | null;
};

export type LowRatingCategory =
  | 'pickup_and_return_rating'
  | 'car_condition_rating'
  | 'cleanliness_rating';

export type LowRatedReview = {
  reservationId: number;
  tripDate: string;
  score: number;
  pickupAndReturnRating: number | null;
  carConditionRating: number | null;
  cleanlinessRating: number | null;
};

export type DashboardCarRatingRowDisplay = {
  licensePlate: string;
  vehicleYear: number | null;
  brandName: string;
  modelName: string;
  imageUrl: string | null;
};

export type LowRatedCar = DashboardCarRatingRowDisplay & {
  id: number;
  name: string;
  averageScore: number | null;
  pickupAndReturnRating: number | null;
  carConditionRating: number | null;
  cleanlinessRating: number | null;
  reviewCount: number;
  lowCategories: LowRatingCategory[];
};

export type DashboardPerformance = {
  period: {
    from: string;
    to: string;
    previousFrom: string;
    previousTo: string;
    timeZone: string;
  };
  earningsByMonth: EarningsMonth[];
  missedEarnings: MissedEarnings;
  completedDailyRentalCount: number;
  completedSubscriptionTripCount: number;
  ratings: {
    current: RatingWindow;
    previous: RatingWindow;
    byMonth: {
      month: string;
      averageScore: number | null;
      reviewCount: number;
    }[];
    distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
  };
  lowRatedCars: LowRatedCar[];
};

export type PerformanceDateRange = {
  from: string;
  to: string;
};

export type DashboardReviewPeriod = PerformanceDateRange & {
  timeZone?: string;
};

/** Matches backend dashboard period boundaries (`period.time_zone`). */
export const DASHBOARD_PERIOD_TIME_ZONE = 'America/New_York';

type Variables = {
  from: string;
  to: string;
  carId?: number;
};

export function getPerformancePeriod(monthCount: number): PerformanceDateRange {
  const end = DateTime.now().setZone(DASHBOARD_PERIOD_TIME_ZONE);
  const start = end.minus({ months: monthCount - 1 }).startOf('month');

  return {
    from: start.toISODate() ?? '',
    to: end.toISODate() ?? '',
  };
}

export const useDashboardPerformance = createQuery<
  DashboardPerformance,
  Variables,
  AxiosError
>({
  queryKey: ['dashboard/performance'],
  fetcher: (variables) => {
    return client
      .get('v2/internal/partners/dashboard/performance', {
        params: {
          from: variables.from,
          to: variables.to,
          ...(variables.carId ? { car_id: variables.carId } : {}),
        },
      })
      .then((response) => response.data.data);
  },
});
